import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';
import type {
    DismissVacancyComplaintResponse,
    GetVacancyComplaintResponse,
    HideVacancyByModeratorRequest,
    HideVacancyByModeratorResponse,
    ListVacancyComplaintsFilters,
    ListVacancyComplaintsResponse
} from '@/lib/vacancyComplaintsSchemas';
import {
    DismissVacancyComplaintResponseSchema,
    GetVacancyComplaintResponseSchema,
    HideVacancyByModeratorResponseSchema,
    ListVacancyComplaintsResponseSchema
} from '@/lib/vacancyComplaintsSchemas';

/**
 * RTK Query endpoints для admin-backend /vacancy-complaints/*
 * (фича staff-hiring Ф2, пост-модерация жалоб на вакансии).
 * admin-backend на порту 5002 без префикса /admin.
 *
 * Контракты — namespace `AdminVacancyComplaints` (27.0.0). Автор жалобы
 * виден только в детали (MF-7); действия модератора (hide-vacancy/dismiss)
 * возвращают обновлённую жалобу — UI перерисовывает статус без рефетча.
 */
export const vacancyComplaintsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listVacancyComplaints: build.query<
            ListVacancyComplaintsResponse,
            ListVacancyComplaintsFilters | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.status) {
                    search.status = params.status;
                }
                if (params?.limit !== undefined) {
                    search.limit = String(params.limit);
                }
                if (params?.offset !== undefined) {
                    search.offset = String(params.offset);
                }
                return {
                    url: '/vacancy-complaints',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): ListVacancyComplaintsResponse =>
                parseWithSchema(ListVacancyComplaintsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((c) => ({
                            type: 'VacancyComplaint' as const,
                            id: c.id
                        })),
                        { type: 'VacancyComplaint' as const, id: 'LIST' }
                    ]
                    : [{ type: 'VacancyComplaint' as const, id: 'LIST' }]
        }),

        getVacancyComplaint: build.query<GetVacancyComplaintResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/vacancy-complaints/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetVacancyComplaintResponse =>
                parseWithSchema(GetVacancyComplaintResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'VacancyComplaint' as const, id: arg.id }
            ]
        }),

        hideVacancyByModerator: build.mutation<
            HideVacancyByModeratorResponse,
            { id: string; body: HideVacancyByModeratorRequest }
        >({
            query: ({ id, body }) => ({
                url: `/vacancy-complaints/${id}/hide-vacancy`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): HideVacancyByModeratorResponse =>
                parseWithSchema(HideVacancyByModeratorResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'VacancyComplaint', id: arg.id },
                { type: 'VacancyComplaint', id: 'LIST' }
            ]
        }),

        dismissVacancyComplaint: build.mutation<
            DismissVacancyComplaintResponse,
            { id: string }
        >({
            query: ({ id }) => ({
                url: `/vacancy-complaints/${id}/dismiss`,
                method: 'POST',
                body: {}
            }),
            transformResponse: (raw: unknown): DismissVacancyComplaintResponse =>
                parseWithSchema(DismissVacancyComplaintResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'VacancyComplaint', id: arg.id },
                { type: 'VacancyComplaint', id: 'LIST' }
            ]
        })
    })
});

export const {
    useListVacancyComplaintsQuery,
    useGetVacancyComplaintQuery,
    useHideVacancyByModeratorMutation,
    useDismissVacancyComplaintMutation
} = vacancyComplaintsApi;
